import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, 
  Newspaper, 
  Users, 
  Trophy, 
  Mail, 
  ChevronRight,
  TrendingUp,
  Clock,
  Package,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getMatches, getNews, getPlayers, getStandings, getContacts, getOrders } from "@/lib/api";

const statCards = [
  { label: "Wedstrijden", icon: Calendar, path: "/admin/matches", color: "bg-blue-500", lightColor: "bg-blue-50", textColor: "text-blue-600" },
  { label: "Nieuws", icon: Newspaper, path: "/admin/news", color: "bg-emerald-500", lightColor: "bg-emerald-50", textColor: "text-emerald-600" },
  { label: "Spelers", icon: Users, path: "/admin/players", color: "bg-violet-500", lightColor: "bg-violet-50", textColor: "text-violet-600" },
  { label: "Ranglijst", icon: Trophy, path: "/admin/standings", color: "bg-amber-500", lightColor: "bg-amber-50", textColor: "text-amber-600" },
  { label: "Berichten", icon: Mail, path: "/admin/contacts", color: "bg-rose-500", lightColor: "bg-rose-50", textColor: "text-rose-600" },
];

const quickActions = [
  { label: "Wedstrijd toevoegen", path: "/admin/matches", icon: Calendar },
  { label: "Nieuws toevoegen", path: "/admin/news", icon: Newspaper },
  { label: "Speler toevoegen", path: "/admin/players", icon: Users },
  { label: "Ranglijst bijwerken", path: "/admin/standings", icon: Trophy },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    matches: 0,
    news: 0,
    players: 0,
    standings: 0,
    messages: 0,
    unreadMessages: 0,
    orders: 0,
    pendingOrders: 0,
  });
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [matchesRes, newsRes, playersRes, standingsRes, contactsRes, ordersRes] = await Promise.all([
          getMatches(),
          getNews(),
          getPlayers(),
          getStandings(),
          getContacts(),
          getOrders().catch(() => ({ data: [] })),
        ]);

        setStats({
          matches: matchesRes.data.length,
          news: newsRes.data.length,
          players: playersRes.data.length,
          standings: standingsRes.data.length,
          messages: contactsRes.data.length,
          unreadMessages: contactsRes.data.filter(c => !c.is_read).length,
          orders: ordersRes.data.length,
          pendingOrders: ordersRes.data.filter(o => o.status === 'pending').length,
        });
        
        setRecentNews(newsRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getStatValue = (label) => {
    switch (label) {
      case "Wedstrijden": return stats.matches;
      case "Nieuws": return stats.news;
      case "Spelers": return stats.players;
      case "Ranglijst": return stats.standings;
      case "Berichten": return stats.messages;
      default: return 0;
    }
  };

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-black text-slate-900 font-['Chivo'] tracking-tight">Dashboard</h1>
        <p className="text-slate-500 font-['Manrope'] mt-1">Welkom bij het Futsal Gent admin panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const value = getStatValue(stat.label);
          return (
            <Link key={stat.label} to={stat.path}>
              <Card 
                className="group hover:shadow-lg transition-all duration-200 border-slate-200 bg-white overflow-hidden" 
                data-testid={`stat-${stat.label.toLowerCase()}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-500 text-sm font-medium font-['Manrope']">{stat.label}</p>
                      <p className="text-3xl font-black text-slate-900 font-['Chivo'] mt-1">
                        {loading ? (
                          <span className="inline-block w-10 h-8 bg-slate-200 rounded animate-pulse" />
                        ) : value}
                      </p>
                      {stat.label === "Berichten" && stats.unreadMessages > 0 && (
                        <Badge className="mt-2 bg-rose-100 text-rose-600 hover:bg-rose-100">
                          {stats.unreadMessages} ongelezen
                        </Badge>
                      )}
                    </div>
                    <div className={`p-3 rounded-xl ${stat.lightColor} group-hover:scale-110 transition-transform duration-200`}>
                      <Icon className={`h-5 w-5 ${stat.textColor}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="lg:col-span-2 border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-slate-900 font-['Chivo'] font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Snelle Acties
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link 
                    key={action.label}
                    to={action.path} 
                    className="flex items-center justify-between p-4 bg-slate-50 hover:bg-blue-50 rounded-xl transition-colors group"
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm group-hover:bg-blue-100 transition-colors">
                        <Icon className="h-4 w-4 text-slate-600 group-hover:text-blue-600" />
                      </div>
                      <span className="font-medium text-slate-700 font-['Manrope'] group-hover:text-blue-700">
                        {action.label}
                      </span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Orders Summary */}
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-slate-900 font-['Chivo'] font-bold flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Bestellingen
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg">
                    <Clock className="h-4 w-4 text-amber-600" />
                  </div>
                  <span className="font-medium text-slate-700 font-['Manrope']">In afwachting</span>
                </div>
                <span className="text-2xl font-black text-amber-600 font-['Chivo']">
                  {loading ? "..." : stats.pendingOrders}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg">
                    <Package className="h-4 w-4 text-slate-600" />
                  </div>
                  <span className="font-medium text-slate-700 font-['Manrope']">Totaal</span>
                </div>
                <span className="text-2xl font-black text-slate-700 font-['Chivo']">
                  {loading ? "..." : stats.orders}
                </span>
              </div>
              <Link 
                to="/admin/orders"
                className="flex items-center justify-center gap-2 p-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors font-['Manrope'] font-medium"
              >
                <Eye className="h-4 w-4" />
                Bekijk alle bestellingen
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent News */}
      {recentNews.length > 0 && (
        <Card className="mt-6 border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-900 font-['Chivo'] font-bold flex items-center gap-2">
                <Newspaper className="h-5 w-5 text-emerald-600" />
                Laatste Nieuws
              </CardTitle>
              <Link 
                to="/admin/news"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium font-['Manrope'] flex items-center gap-1"
              >
                Bekijk alles <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {recentNews.map((article, index) => (
                <Link
                  key={article.id || index}
                  to="/admin/news"
                  className="flex items-center gap-4 p-5 hover:bg-slate-50 transition-colors"
                >
                  {article.image_url ? (
                    <img 
                      src={article.image_url} 
                      alt={article.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Newspaper className="h-6 w-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 font-['Manrope'] truncate">{article.title}</p>
                    <p className="text-sm text-slate-500 truncate mt-1">{article.summary}</p>
                  </div>
                  <Badge variant="outline" className="shrink-0 border-slate-200 text-slate-500">
                    {article.category}
                  </Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Site Link */}
      <div className="mt-8 text-center">
        <Link 
          to="/" 
          target="_blank"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold font-['Manrope'] transition-colors"
          data-testid="view-site-link"
        >
          Bekijk publieke website
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
